import type {UseCountdownOptions, UseCountdownResult} from '../types';

import {useCallback, useEffect, useRef, useState} from 'react';

export function useCountdown({
  initialSeconds,
  autoStart = true,
  onExpire
}: UseCountdownOptions): UseCountdownResult {
  const [remaining, setRemaining] = useState(Math.max(0, initialSeconds));
  const [running, setRunning] = useState(autoStart);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    setRemaining(Math.max(0, initialSeconds));
    if (autoStart) {
      setRunning(true);
    }
  }, [initialSeconds, autoStart]);

  useEffect(() => {
    if (!running || remaining <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          setRunning(false);
          onExpireRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [running, remaining]);

  const reset = useCallback(
    (seconds = initialSeconds) => {
      setRemaining(Math.max(0, seconds));
      setRunning(true);
    },
    [initialSeconds]
  );

  const start = useCallback(() => {
    if (remaining > 0) {
      setRunning(true);
    }
  }, [remaining]);

  const pause = useCallback(() => {
    setRunning(false);
  }, []);

  return {
    remaining,
    isExpired: remaining <= 0,
    reset,
    start,
    pause
  };
}
